import { BellOutlined, DownOutlined } from "@ant-design/icons";
import { Badge, Button, Dropdown, Empty, notification, Spin } from "antd";
import {
  FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  memo,
} from "react";
import styles from "./NotificationMenuItem.module.less";
import { useAddSocketListener } from "~/hooks/useAddSocketListener";
import { STEEL_SOCKET_URL } from "~/constants/url";
import { COPOLIT_SOCKET_KEY } from "~/constants/socket";
import { useNotificationHistory } from "~/hooks/useNotificationHistory";
import { COPILOT_EVENTS } from "~/constants/events";
import classNames from "classnames";
import { useLanguage } from "~/hooks/useLanguage";
import { FixedSizeList as List } from "react-window";
import { CopilotMessageContext } from "~/providers/CopilotMessageProvider";
import { getConversationByResponseId } from "~/pages/copilotMessage/services/copilot";
import { isConversationGeneric } from "~/pages/copilotMessage/components/conversationDetail/components/conversationMessages/components/mikeMessage/helpers/mikeMessageParametersHelper";

const NOTIFICATIONS_LIMIT = 10;
const NOTIFICATION_ITEM_HEIGHT = 115;

interface NotificationItemProps {
  data: {
    notifications: NotificationType[];
    handleNotificationClick: (id: string) => void;
    formatDate: (date: string) => string;
    formatTime: (date: string) => string;
  };
  index: number;
  style: React.CSSProperties;
}

type NotificationType = {
  id: string;
  isAcknowledged: boolean;
  alertType: string;
  campaign?: string;
  heat?: string;
  operationDate?: string;
  conversationId?: string;
  conversationResponseId?: string;
};

const NotificationRow = memo(
  ({ data, index, style }: NotificationItemProps) => {
    const { notifications, handleNotificationClick, formatDate, formatTime } =
      data;

    const notification = notifications[index];

    return (
      <div
        style={style}
        className={classNames(
          styles.notificationItem,
          !notification.isAcknowledged && styles.unreadNotification,
        )}
        onClick={() => handleNotificationClick(notification.id)}
      >
        <div className={styles.notificationContent}>
          <div className={styles.notificationTitle}>
            <span className={styles.notificationMessage}>
              <span>{notification.alertType}</span>
            </span>
          </div>
          <div className={styles.notificationSubtitle}>
            {notification.campaign && (
              <span>
                Campaign: <strong>{notification.campaign}</strong>
              </span>
            )}
            {notification.heat && (
              <span>
                {" "}
                Heat: <strong>{notification.heat}</strong>
              </span>
            )}
          </div>
          <div className={styles.notificationTime}>
            {notification.operationDate
              ? `${formatDate(notification.operationDate)} ${formatTime(
                  notification.operationDate,
                )}`
              : ""}
          </div>
        </div>

        {!notification.isAcknowledged && (
          <div className={styles.unreadBadgeContainer}>
            <span className={styles.unreadBadge} />
          </div>
        )}
      </div>
    );
  },
);

const LoadMoreButton = memo(
  ({ onClick }: { onClick: (e: React.MouseEvent) => void }) => {
    return (
      <div className={styles.loadMore} onClick={onClick}>
        Load More <DownOutlined />
      </div>
    );
  },
);

const NotificationMenuItem: FC = () => {
  const { formatDate, formatTime } = useLanguage();
  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<List>(null);
  const [displayLimit, setDisplayLimit] = useState<number>(NOTIFICATIONS_LIMIT);

  const { fetchNotifications, notifications, notificationsLoading } =
    useNotificationHistory();

  const { conversationList, setSelectedConversation, setConversationResponse } =
    useContext(CopilotMessageContext);

  const displayedNotifications = useMemo<NotificationType[]>(() => {
    // Ensure all notifications have required NotificationType fields
    return notifications.slice(0, displayLimit).map((n) => ({
      id: n.id,
      isAcknowledged: n.isAcknowledged ?? false,
      alertType: n.alertType ?? "",
      campaign: n.campaign,
      heat: n.heat,
      operationDate: n.operationDate,
      conversationId: n.conversationId ?? "",
      conversationResponseId: n.conversationResponseId ?? "",
    }));
  }, [notifications, displayLimit]);

  const unreadCount = useMemo(
    () => notifications.filter((n) => !n.isAcknowledged).length,
    [notifications],
  );

  const hasMoreNotifications = useMemo(
    () => notifications.length > displayLimit,
    [notifications, displayLimit],
  );

  const handleLoadMore = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setDisplayLimit((prev) => prev + NOTIFICATIONS_LIMIT);

    if (listRef.current) {
      const listInstance = listRef.current as List & {
        state?: { scrollOffset?: number };
      };
      const currentOffset = listInstance.state?.scrollOffset || 0;
      setTimeout(() => {
        if (listRef.current) {
          listRef.current.scrollTo(currentOffset);
        }
      }, 0);
    }
  }, []);

  const onAlertRaised = useCallback(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  useAddSocketListener(STEEL_SOCKET_URL, COPOLIT_SOCKET_KEY, {
    OnCopilotAlertRaised: onAlertRaised,
  });

  useEffect(() => {
    fetchNotifications();
    window.addEventListener(
      COPILOT_EVENTS.NOTIFICATION_ACKNOWLEDGED,
      fetchNotifications as EventListener,
    );

    return () => {
      window.removeEventListener(
        COPILOT_EVENTS.NOTIFICATION_ACKNOWLEDGED,
        fetchNotifications as EventListener,
      );
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const listKey = useMemo(
    () =>
      `notification-list-${displayedNotifications.map((n) => n.id).join("-")}`,
    [displayedNotifications],
  );

  useEffect(() => {
    if (listRef.current) {
      const listInstance = listRef.current as List & {
        resetAfterIndex?: (index: number) => void;
      };
      listInstance.resetAfterIndex?.(0);
    }
  }, [notifications.length]);

  const handleNotificationClick = useCallback(
    async (id: string) => {
      try {
        const currentNotification = notifications.find(
          (notification) => notification.id === id,
        );

        if (!currentNotification || !currentNotification.conversationId) return;

        if (isConversationGeneric(currentNotification.conversationId)) {
          // First, call ConversationByResponseId/{responseId} service
          const responseId = currentNotification.conversationResponseId;
          if (responseId) {
            const conversation = await getConversationByResponseId(responseId);

            // set it as selected conversation
            if (conversation) {
              setSelectedConversation(conversation);
            }
          }
        } else {
          // For other conversationIds, use the existing logic
          const relatedConversation = conversationList?.find(
            (x) => x.id == currentNotification.conversationId,
          );

          if (relatedConversation) {
            setSelectedConversation(relatedConversation);
          }
        }

        setConversationResponse({
          conversationId: currentNotification.conversationId,
          responseId: currentNotification.conversationResponseId,
        });
      } catch (error) {
        notification.error({
          message: "Error handling notification click",
          description:
            error instanceof Error ? error.message : "Unknown error occurred",
        });
      }
    },
    [
      notifications,
      conversationList,
      setSelectedConversation,
      setConversationResponse,
    ],
  );

  const itemData = useMemo(
    () => ({
      notifications: displayedNotifications,
      handleNotificationClick,
      formatDate,
      formatTime,
    }),
    [displayedNotifications, handleNotificationClick, formatDate, formatTime],
  );

  const dropdownItems = [
    {
      key: "notifications",
      className: styles.p0,

      label: (
        <div className={styles.notificationContainer} ref={containerRef}>
          {notifications.length > 0 ? (
            <div className={styles.notificationWrapper}>
              <List
                key={listKey}
                ref={listRef}
                className={styles.virtualList}
                height={400}
                width="100%"
                itemCount={displayedNotifications.length}
                itemSize={NOTIFICATION_ITEM_HEIGHT}
                itemData={itemData}
                overscanCount={5}
              >
                {NotificationRow}
              </List>
              {hasMoreNotifications && (
                <div className={styles.loadMoreContainer}>
                  <LoadMoreButton onClick={handleLoadMore} />
                </div>
              )}
            </div>
          ) : (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="No Notifications"
              className={styles.noNotifications}
            />
          )}
        </div>
      ),
    },
  ];

  return (
    <div className={styles.NotificationMenuItem}>
      {notificationsLoading ? (
        <Spin />
      ) : (
        <Dropdown
          menu={{
            items: dropdownItems,
          }}
          placement="bottomRight"
          trigger={["click"]}
          dropdownRender={(menu) => (
            <div>
              <div className={styles.notificationHeader}>
                <span>Notifications</span>
              </div>
              {menu}
            </div>
          )}
        >
          <div className={styles.badgeContainer}>
            <Badge count={unreadCount} size="small">
              <Button type="text" icon={<BellOutlined />} />
            </Badge>
          </div>
        </Dropdown>
      )}
    </div>
  );
};

export default NotificationMenuItem;
